import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import values from 'lodash/values';
import find from 'lodash/find';

import * as Types from 'types';
import { showErrorModal } from 'features/modals/actions';
import { withPlayerDispatcher, withPlayerDispatcherProps } from 'player/withPlayerDispatcher';

import { PlaylistTitle } from 'playlists/PlaylistTitle';
import { PlaylistInfobar } from 'playlists/components/PlaylistInfobar';

import {
  setPlaylists,
  destroyPlaylist,
  receivePlaylists,
  setCurrentPlaylist,
  savePlaylist,
} from 'features/playlists/actions';
import { isPlaylistSaved, getPlaylistCompatibilities } from 'features/playlists/helpers';

import { ButtonsDefault } from './ButtonsDefault';
import { ButtonsEditMode } from './ButtonsEditMode';

type StateProps = {
  unmodifiedPlaylist: Types.Playlist;
  masterPlaylistId?: string;
};

type DispatchProps = {
  setCurrentPlaylist: typeof setCurrentPlaylist;
  setPlaylists: typeof setPlaylists;
  destroyPlaylist: (playlistId: string) => Promise<void>;
  savePlaylist: (playlist: Types.Playlist) => Promise<Types.Playlist>;
  receivePlaylists: typeof receivePlaylists;
  showErrorModal: typeof showErrorModal;
};

type OwnProps = {
  playlist: Types.Playlist;
  currentUserId: number;
};

type HOCProps = withPlayerDispatcherProps & RouteComponentProps;

type Props = StateProps & DispatchProps & OwnProps & HOCProps;

class PlaylistToolbarComponent extends React.PureComponent<Props> {
  constructor(props: Props) {
    super(props);

    this.changeName = this.changeName.bind(this);
    this.save = this.save.bind(this);
    this.onPlayClick = this.onPlayClick.bind(this);
    this.cancel = this.cancel.bind(this);
  }

  onPlayClick() {
    const { playlist } = this.props;
    this.props.setPlaylists([playlist]);
    this.props.play(playlist.id, playlist.items[0].id);
  }

  changeName(name: string) {
    this.props.setCurrentPlaylist({
      ...this.props.playlist,
      name,
    });
  }

  removePlaylist(playlistId: string) {
    this.props.destroyPlaylist(playlistId);
    this.props.history.push(Routes.vault_playlists_path());
  }

  save() {
    const { playlist, savePlaylist } = this.props;
    savePlaylist(playlist)
      .then((savedPlaylist) => {
        if (window.location.pathname !== Routes.vault_playlist_path(savedPlaylist.id)) {
          this.props.history.replace(Routes.vault_playlist_path(savedPlaylist.id));
        }
      })
      .catch(() => {
        this.props.showErrorModal(
          'Unable to save playlist, please try again.',
          Routes.edit_vault_playlist_path(playlist.id),
        );
      });

    this.props.history.push(Routes.vault_playlist_path(playlist.id));
  }

  cancel() {
    let { unmodifiedPlaylist, playlist, history } = this.props;
    if (isPlaylistSaved(playlist)) {
      history.push(Routes.vault_playlist_path(unmodifiedPlaylist.id));
      this.props.setCurrentPlaylist(unmodifiedPlaylist);
    } else if (this.props.masterPlaylistId) {
      history.push(Routes.vault_playlist_path(this.props.masterPlaylistId));
    }
    // this.props.setCurrentPlaylist(unmodifiedPlaylist);
    // this.props.history.push(Routes.vault_playlist_path(unmodifiedPlaylist.id));
  }

  render() {
    const { playlist, currentUserId } = this.props;
    const playlistId: number | undefined = Number.isFinite(parseInt(playlist.id, 10))
      ? parseInt(playlist.id, 10)
      : undefined;
    const playlistCompatibilities = getPlaylistCompatibilities(playlist, currentUserId);

    const isEditMode = window.location.pathname === Routes.edit_vault_playlist_path(playlist.id);

    const showRemovePlaylist =
      isEditMode && isPlaylistSaved(playlist) && playlistCompatibilities === 'editable';

    return (
      <div className='playlist-header playlist-toolbar'>
        <div className='row'>
          <div className='col-12 col-sm'>
            <PlaylistTitle
              playlist={playlist}
              canEdit={isEditMode && playlistCompatibilities === 'editable'}
              onChange={this.changeName}
            />
          </div>
          <div className='col-12 order-sm-last mb-1'>
            <PlaylistInfobar
              playlist={playlist}
              showRemove={showRemovePlaylist}
              onRemove={() => this.removePlaylist(playlist.id)}
            />
          </div>
          <div className='col-12 col-sm-auto mb-2 mb-sm-0'>
            <div className='row no-gutters-sm'>
              {isEditMode ? (
                <ButtonsEditMode onCancel={this.cancel} onSave={this.save} />
              ) : (
                <ButtonsDefault
                  onPlay={this.onPlayClick}
                  playlistId={playlistId}
                  playlist={this.props.playlist}
                  compatibilities={playlistCompatibilities}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ playlists: { byId } }: Types.RootState, props: OwnProps) => {
  const masterPlaylist = find(values(byId), (playlist) => playlist.type === 'Master');

  return {
    unmodifiedPlaylist: byId[props.playlist.id],
    masterPlaylistId: masterPlaylist ? masterPlaylist.id : undefined,
  };
};

const mapDispatchToProps = {
  setPlaylists,
  destroyPlaylist,
  receivePlaylists,
  showErrorModal,
  setCurrentPlaylist,
  savePlaylist,
};

export const PlaylistToolbar = withPlayerDispatcher(
  connect(mapStateToProps, mapDispatchToProps)(PlaylistToolbarComponent),
);
